import React, { useEffect } from 'react';
import styles from './index.module.scss';
import IOsPermissionModal from './IOsPermissionModal';
import ButtonWithArrowIcon from 'components/Buttons/ButtonWithArrowIcon';
import { ScanPageWizardStep2 } from 'routes/paths';
import { useNavigate } from 'react-router-dom';
import StepDescription from '../StepDescription';
import {
  getCameraPermission,
  requestCameraPermission,
} from 'functions/browserPermissions';
import ManualUploadButton from 'features/Driver/ManualUploadButton';
import { useLogsManager } from 'integrations/logs';
import { DocumentTypeEnum } from 'common/document/enums/document-type.enum';

export interface CameraPermissionProps {
  documentRequestId: string;
  documentType: string;
  documentId: string;
  documentTypeEnum: DocumentTypeEnum;
}

function CameraPermission({
  documentRequestId,
  ...props
}: CameraPermissionProps) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [wasDenied, setWasDenied] = React.useState<boolean>(false);
  const [wasGranted, setWasGranted] = React.useState<boolean>(false);
  const [attemps, setAttemps] = React.useState<number>(0);
  const logs = useLogsManager();

  const checkCameraAccess = async () => {
    setAttemps((x) => x + 1);

    if (attemps >= 2) {
      setWasDenied(true);
      return;
    }

    setIsLoading(true);

    try {
      await requestCameraPermission();
    } catch (error) {
      console.error(error);
    } finally {
      const permission = await getCameraPermission();
      if (permission === 'granted') {
        navigate(
          ScanPageWizardStep2.replace(':documentRequestId', documentRequestId)
            .replace(':documentType', props.documentType)
            .replace(':documentTypeEnum', props.documentTypeEnum || '')
            .replace(':documentId', props.documentId)
        );
      } else if (permission === 'denied') {
        logs.logWarning('Camera permission was denied');
        setWasDenied(true);
      }
    }

    setIsLoading(false);
  };

  useEffect(() => {
    (async () => {
      const permission = await getCameraPermission();
      if (permission === 'granted') {
        {
          setWasGranted(true);
        }
      } else if (permission === 'denied') {
        setWasDenied(true);
      }
    })();
  }, []);

  return (
    <div className={styles.root}>
      {!wasGranted && !wasDenied && (
        <>
          <StepDescription
            title="Allow camera access"
            description="We need camera access to scan your documents. You are about to be prompted with a popup. Please click ok"
          />

          <IOsPermissionModal />
        </>
      )}

      {wasGranted && (
        <StepDescription
          title="Camera access granted"
          description="You can now continue to the next step"
        />
      )}

      {wasDenied && (
        <StepDescription
          title="Camera access was denied"
          description="Enable the camera permission manually to continue or click the button below to upload your documents manually"
        />
      )}

      <div className={styles.actions}>
        {!wasDenied && (
          <ButtonWithArrowIcon
            variant="contained"
            disabled={isLoading}
            color="primary"
            onClick={checkCameraAccess}
          >
            {!wasGranted && (
              <> {isLoading ? 'Checking camera...' : 'Enable Camera'}</>
            )}
            {wasGranted && 'Continue'}
          </ButtonWithArrowIcon>
        )}

        <div>
          <ManualUploadButton
            withTimeout={!wasDenied}
            documentRequestId={documentRequestId}
            documentId={props.documentId}
            documentType={props.documentType}
            documentTypeEnum={props.documentTypeEnum}
          />
        </div>
      </div>
    </div>
  );
}

export default CameraPermission;
